import Navbar from 'components/navbar';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { commaSeparatedNumbers } from '../../utils/commaSeparatedNumbers';
import classes from './visibilityBoardRevamp.module.scss';

// PrimeReact components
import { PrinterIcon } from 'lucide-react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useVisibilityBoardRevamp } from './useVisibilityBoardRevamp';

const VisibilityBoardRevamp: React.FC = () => {
  const {
    selectedRoom,
    selectedShift,
    setSelectedRoom,
    setSelectedShift,
    data,
    handlePrint,
    contentRef,
    totalBph,
    transformedCleanData,
    isLoading,
    roomOptions,
    scheduleOptions,
    roomOptionsLoading,
    scheduleOptionsLoading,
    selectedRoomName,
    currentDate
  } = useVisibilityBoardRevamp();

  return (
    <div className={classes.VisibilityBoardComponent}>
      {/* This will not be included in printing */}
      <div className={classes.screenOnly}>
        <Navbar heading="Visibility Board" />
      </div>

      {/* Top Bar with Dropdowns and Print Button - hidden during print */}
      <div className={`${classes.topBar} ${classes.screenOnly}`}>
        <div className={classes.dropdowns}>
          <div className={classes.dropdown}>
            <label>Shift:</label>
            <Dropdown
              value={selectedShift}
              options={scheduleOptions}
              onChange={e => setSelectedShift(e.value)}
              placeholder="Select Shift"
              className="p-inputtext-sm"
              loading={scheduleOptionsLoading}
            />
          </div>
          <div className={classes.dropdown}>
            <label>Room:</label>
            <Dropdown
              value={selectedRoom}
              options={roomOptions}
              onChange={e => setSelectedRoom(e.value)}
              placeholder="Select Room"
              className="p-inputtext-sm"
              loading={roomOptionsLoading}
            />
          </div>
        </div>
        <Button
          id="print-btn"
          icon={<PrinterIcon color="#4D2E6D" />}
          rounded
          style={{ backgroundColor: '#D6B1FD', border: 'none' }}
          onClick={handlePrint}
          disabled={isLoading || roomOptionsLoading || scheduleOptionsLoading}
        />
      </div>

      {isLoading || roomOptionsLoading || scheduleOptionsLoading ? (
        <div className={classes.loadingContainer}>
          <ProgressSpinner />
          <p>Loading data...</p>
        </div>
      ) : data.bph.length === 0 && data.clean.length === 0 ? (
        <div className={classes.noData}>
          <h1>No data available</h1>
        </div>
      ) : (
        /* This div will be cloned for printing, with all its contents */
        <div id="printableContent" className={classes.printableContent} ref={contentRef}>
          <div className={classes.mainContainer} id="mainContainer">
            {/* Left Section: Date & Time Table + Bottles Per Hour Table + Comments */}
            <div className={classes.leftSection} id="leftSection">
              <div className={classes.topSection} id="topSection">
                {/* Combined Bottles Per Hour Table */}
                <table className={classes.bottleTable} id="bottleTable">
                  <thead>
                    <tr>
                      <td className={classes.bottleHeader} colSpan={6} id="bottleHeader">
                        BOTTLES PER HOUR
                      </td>
                    </tr>
                    <tr className={classes.bottleSubheader} id="bottleSubheader">
                      <td className={classes.dateTimeHeader} rowSpan={2}>Date/Time</td>
                      <td colSpan={5} className={classes.inProcessHeader}>
                        <div className={classes.inProcessContent}>
                          <span> # of bottles for In-process Job:</span>
                          <span className={classes.inProcessValue}>
                            {data.numberOfBottlesInAtShiftStart !== undefined && data.numberOfBottlesInAtShiftStart !== null
                              ? commaSeparatedNumbers(data.numberOfBottlesInAtShiftStart)
                              : '-'}
                          </span>
                        </div>
                      </td>
                    </tr>
                    <tr className={classes.bottleSubheader2} id="bottleSubheader2">
                      <td className={classes.jobIdHeader}>Job ID</td>
                      <td colSpan={2} className={classes.r2Header}>R2</td>
                      <td>Actual</td>
                      <td>Comments</td>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className={classes.inProcessJobRow} id="inProcessJobRow">
                      <td>
                        {data.bph[0]?.time ? (
                          <>
                            {data.bph[0].time.split(' ')[0].replace(/-/g, '/')}
                            <br />
                            {data.bph[0].time.split(' ').slice(1).join(' ')}
                          </>
                        ) : ''}
                      </td>
                      <td className={classes.jobCell}>{data.bph[0]?.activeJobs[0] || ''}</td>
                      <td className={classes.r2ValueCell}>{data.bph[0]?.totalBphOfJobs ? commaSeparatedNumbers(data.bph[0]?.totalBphOfJobs) : ''}</td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>
                    {data.bph.slice(1).map((item, index) => (
                      <tr key={`${item.time}`} className={classes.r2Row} id="r2Row">
                        <td>
                          {item.time.split(' ')[0].replace(/-/g, '/')}
                          <br />
                          {item.time.split(' ').slice(1).join(' ')}
                        </td>
                        <td className={classes.jobCell}>{item.activeJobs[0] || ''}</td>
                        <td className={classes.r2ValueCell}>{item.totalBphOfJobs ? commaSeparatedNumbers(item.totalBphOfJobs) : ''}</td>
                        <td></td>
                        <td>{item.actual || ''}</td>
                        <td>{item.reason || ''}</td>
                      </tr>
                    ))}
                    <tr className={classes.spacedRow} id="spacedRow">
                      <td></td>
                      <td>SHIFT TOTAL</td>
                      <td className={classes.r2ValueCell}>{totalBph ? commaSeparatedNumbers(totalBph) : ''}</td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Comments Table - Static table for manual filling */}
              <table className={classes.commentsTable} id="commentsTable">
                <thead>
                  <tr>
                    <th>Additional Comments</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className={classes.commentLine}>
                    <td>&nbsp;</td>
                  </tr>
                  <tr className={classes.commentLine}>
                    <td>&nbsp;</td>
                  </tr>
                  <tr className={classes.commentLine}>
                    <td>&nbsp;</td>
                  </tr>
                  <tr className={classes.commentLine}>
                    <td>&nbsp;</td>
                  </tr>
                  <tr className={classes.commentLine}>
                    <td>&nbsp;</td>
                  </tr>
                  <tr className={classes.commentLine}>
                    <td>&nbsp;</td>
                  </tr>
                  <tr className={classes.commentLine}>
                    <td>&nbsp;</td>
                  </tr>
                  <tr className={classes.commentLine}>
                    <td>&nbsp;</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Spacer */}
            <div className={classes.spacer}></div>

            {/* Right Section: Changeovers */}
            <div className={classes.rightSection} id="rightSection">
              <table className={classes.changeoversTable} id="changeoversTable">
                <thead>
                  <tr className={classes.titleRow} id="titleRow">
                    <th colSpan={7}>CHANGE OVERS</th>
                  </tr>
                </thead>
                <tbody>
                  {transformedCleanData.length > 0 ? (
                    <>
                      {transformedCleanData.slice(0, 4).map((cleanItem, cleanIndex) => (
                        <React.Fragment key={`${cleanItem.cleanId}`}>
                          {/* Summary Row */}
                          <tr className={classes.subtitleRow} id="subtitleRow">
                            <td id="job-and-level">Job and Level</td>
                            <td className={classes.lightBg} id="lightBg">
                              Total R2:
                            </td>
                            <td
                              className={`${classes.lightBg} ${classes.dangerText}`}
                              id="dangerText"
                            >
                              {cleanItem.totalChangeoverR2 ? commaSeparatedNumbers(cleanItem.totalChangeoverR2) : ''}
                            </td>
                            <td className={classes.lightBg} id="lightBg">
                              Total Actual:
                            </td>
                            <td className={classes.lightBg} id="lightBg"></td>
                            <td className={classes.lightBg} id="lightBg">
                              Total Var:
                            </td>
                            <td className={classes.lightBg} id="lightBg"></td>
                          </tr>

                          {/* Job ID Row */}
                          <tr className={classes.subtitleRow1}>
                            <td className={classes.lightBg}>{cleanItem.cleanId}</td>
                            <td className={classes.headerCell} id="headerCell">
                              R2
                            </td>
                            <td className={classes.headerCell} id="headerCell">
                              Start Time
                            </td>
                            <td className={classes.headerCell} id="headerCell">
                              Expected Stop Time
                            </td>
                            <td className={classes.headerCell} id="headerCell">
                              Actual Stop Time
                            </td>
                            <td className={classes.headerCell} id="headerCell">
                            Variance
                            </td>
                            <td className={classes.headerCell} id="headerCell">
                              Comments
                            </td>
                          </tr>

                          {/* Clean, Clear, Setup, LV Rows */}
                          {cleanItem.sections.map((section, sectionIndex) => {
                            // Define label map to ensure consistent labels
                            const labelMap: { [key: string]: string } = {
                              'Clean': 'Clean (CL)',
                              'Clear': 'Clear (CR)',
                              'Setup': 'Setup (ST)',
                              'LV': 'Verify (LV)',
                              'Verify': 'Verify (LV)',
                              'Clean (CL)': 'Clean (CL)',
                              'Clear (CR)': 'Clear (CR)',
                              'Setup (ST)': 'Setup (ST)',
                              'Verify (LV)': 'Verify (LV)'
                            };
                            
                            const displayLabel = labelMap[section.label] || section.label;
                            
                            return (
                              <tr key={`${cleanItem.cleanId}-${sectionIndex}`}>
                                <td id="outerAlign">
                                  {displayLabel}
                                </td>
                                <td>{section.value ? section.value : ''}</td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      ))}
                      {/* Render remaining empty rows if data length is less than 4 */}
                      {Array.from({ length: Math.max(0, 4 - Math.min(transformedCleanData.length, 4)) }).map(
                        (_, index) => (
                          <React.Fragment key={`empty-${index}`}>
                            <tr className={classes.subtitleRow} id="subtitleRow">
                              <td id="job-and-level">Job and Level</td>
                              <td className={classes.lightBg} id="lightBg">
                                Total R2:
                              </td>
                              <td
                                className={`${classes.lightBg} ${classes.dangerText}`}
                                id="dangerText"
                              ></td>
                              <td className={classes.lightBg} id="lightBg">
                                Total Actual:
                              </td>
                              <td className={classes.lightBg} id="lightBg"></td>
                              <td className={classes.lightBg} id="lightBg">
                                Total Var:
                              </td>
                              <td className={classes.lightBg} id="lightBg"></td>
                            </tr>

                            <tr className={classes.subtitleRow1}>
                              <td className={classes.lightBg} id="lightBg"></td>
                              <td className={classes.headerCell} id="headerCell">
                                R2
                              </td>
                              <td className={classes.headerCell} id="headerCell">
                                Start Time
                              </td>
                              <td className={classes.headerCell} id="headerCell">
                                Expected Stop Time
                              </td>
                              <td className={classes.headerCell} id="headerCell">
                                Actual Stop Time
                              </td>
                              <td className={classes.headerCell} id="headerCell">
                                Variance
                              </td>
                              <td className={classes.headerCell} id="headerCell">
                                Comments
                              </td>
                            </tr>

                            <tr>
                              <td className={classes.outerAlign} id="outerAlign">
                                Clean (CL)
                              </td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                            </tr>
                            <tr>
                              <td className={classes.outerAlign} id="outerAlign">
                                Clear (CR)
                              </td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                            </tr>
                            <tr>
                              <td className={classes.outerAlign} id="outerAlign">
                                Setup (ST)
                              </td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                            </tr>
                            <tr>
                              <td className={classes.outerAlign} id="outerAlign">
                                Verify (LV)
                              </td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                            </tr>
                          </React.Fragment>
                        )
                      )}
                    </>
                  ) : (
                    // Render 4 empty rows when no data is available
                    Array.from({ length: 4 }).map((_, index) => (
                      <React.Fragment key={`empty-${index}`}>
                        <tr className={classes.subtitleRow} id="subtitleRow">
                          <td id="job-and-level">Job and Level</td>
                          <td className={classes.lightBg} id="lightBg">
                            Total R2:
                          </td>
                          <td
                            className={`${classes.lightBg} ${classes.dangerText}`}
                            id="dangerText"
                          ></td>
                          <td className={classes.lightBg} id="lightBg">
                            Total Actual:
                          </td>
                          <td className={classes.lightBg} id="lightBg"></td>
                          <td className={classes.lightBg} id="lightBg">
                            Total Var:
                          </td>
                          <td className={classes.lightBg} id="lightBg"></td>
                        </tr>

                        <tr className={classes.subtitleRow1} id="subtitleRow1">
                          <td className={classes.lightBg} id="lightBg"></td>
                          <td className={classes.headerCell} id="headerCell">
                            R2
                          </td>
                          <td className={classes.headerCell} id="headerCell">
                            Start Time
                          </td>
                          <td className={classes.headerCell} id="headerCell">
                            Expected Stop Time
                          </td>
                          <td className={classes.headerCell} id="headerCell">
                            Actual Stop Time
                          </td>
                          <td className={classes.headerCell} id="headerCell">
                            Variance
                          </td>
                          <td className={classes.headerCell} id="headerCell">
                            Comments
                          </td>
                        </tr>

                            <tr>
                              <td id="outerAlign">
                                Clean (CL)
                              </td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                            </tr>
                            <tr>
                              <td id="outerAlign">
                                Clear (CR)
                              </td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                            </tr>
                            <tr>
                              <td id="outerAlign">
                                Setup (ST)
                              </td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                            </tr>
                            <tr>
                              <td id="outerAlign">
                                Verify (LV)
                              </td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                            </tr>
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default observer(VisibilityBoardRevamp);
